import React, { useState, useEffect, useMemo } from 'react';
import {
  Clock,
  Target,
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Lightbulb,
  RefreshCw,
  Download,
  Play,
  Award,
  TrendingUp,
  FileText,
  User,
  GraduationCap,
  Briefcase,
  Plane,
  Globe,
  DollarSign
} from 'lucide-react';
import './IrelandVisaInterviewSimulator.css';

// Visa Types
const VISA_TYPES = [
  { id: 'student', name: 'Student Visa (Stamp 2)', icon: '🎓', description: 'For full-time degree programs in Ireland' },
  { id: 'work', name: 'Work Permit (Critical Skills / General)', icon: '💼', description: 'For employment with Irish companies' },
  { id: 'tourist', name: 'Tourist Visa', icon: '🧳', description: 'For short visits and tourism' },
  { id: 'dependent', name: 'Dependent Visa', icon: '👨‍👩‍👧', description: 'For family members of visa holders' },
  { id: 'graduate', name: 'Graduate Scheme (Stamp 1G)', icon: '📜', description: 'For recent graduates seeking employment' }
];

// Question Categories
const QUESTION_CATEGORIES = {
  personal: { name: 'Personal Background', icon: User, color: '#3b82f6' },
  education: { name: 'Education/Course', icon: GraduationCap, color: '#10b981' },
  financial: { name: 'Financial Support', icon: DollarSign, color: '#f59e0b' },
  career: { name: 'Career/Work', icon: Briefcase, color: '#8b5cf6' },
  intent: { name: 'Intent to Return', icon: Plane, color: '#ec4899' },
  ireland: { name: 'Ireland Knowledge', icon: Globe, color: '#169B62' }
};

// Question Bank - 50+ questions specific to Ireland visa interviews
const QUESTION_BANK = [
  // Personal Background (10 questions)
  {
    id: 1,
    category: 'personal',
    question: 'Can you tell me about yourself and your background?',
    sampleAnswer: 'My name is [Name], and I am from [City], India. I completed my [Degree] from [University] with [GPA/Percentage]. I have always been passionate about [Field] and have [X] years of experience in [relevant area]. I am excited about the opportunity to study/work in Ireland because of its excellent education system and thriving [industry] sector.',
    redFlags: ['Giving vague or inconsistent information', 'Mentioning plans to settle permanently abroad', 'Showing lack of clarity about background'],
    tips: 'Keep your answer concise (2-3 minutes). Focus on educational and professional achievements relevant to your visa purpose.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 2,
    category: 'personal',
    question: 'Why do you want to go to Ireland specifically?',
    sampleAnswer: 'Ireland offers world-class education with globally recognized degrees. The country has a strong [industry] sector with companies like Google, Facebook, and Apple having their European headquarters there. The post-study work opportunities through the Third Level Graduate Scheme are excellent, and Ireland\'s welcoming culture and English-speaking environment make it ideal for international students.',
    redFlags: ['Saying "because it\'s easy to get a visa"', 'Mentioning only social benefits', 'Showing no research about Ireland'],
    tips: 'Research Ireland\'s education system, industry connections, and cultural aspects. Connect your answer to your career goals.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 3,
    category: 'personal',
    question: 'Have you traveled abroad before? If yes, where?',
    sampleAnswer: 'Yes, I have traveled to [countries] for [purpose - tourism/conference/training]. I returned to India within the permitted duration and have always complied with visa regulations. OR No, this would be my first international trip, and I am excited about the opportunity to experience Ireland\'s rich culture and education system.',
    redFlags: ['Lying about travel history', 'Overstaying in previous countries', 'Vague answers about past travels'],
    tips: 'Be honest about your travel history. If you haven\'t traveled before, that\'s okay - focus on your genuine purpose for this trip.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 4,
    category: 'personal',
    question: 'Do you have any relatives or friends in Ireland?',
    sampleAnswer: 'No, I do not have any relatives or close friends in Ireland. I am going solely for [study/work/tourism] purposes. OR Yes, I have a [relationship] who lives in [city], but I am fully self-sufficient and not dependent on them for accommodation or financial support.',
    redFlags: ['Hiding family connections', 'Saying you will stay with relatives when you have arranged accommodation', 'Inconsistent information'],
    tips: 'Be transparent about any connections in Ireland. Having relatives doesn\'t hurt your application if you\'re honest.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 5,
    category: 'personal',
    question: 'What do your parents do? How will they support your education?',
    sampleAnswer: 'My father is a [profession] working at [company/organization] with an annual income of [amount]. My mother is a [profession]. They have saved [amount] specifically for my education and have provided bank statements showing sufficient funds to cover my tuition and living expenses for the entire duration of my course.',
    redFlags: ['Not knowing parents\' income details', 'Unable to explain source of funds', 'Inconsistent financial information'],
    tips: 'Know your sponsors\' financial details thoroughly. Carry all supporting documents.',
    difficulty: 'Easy',
    applicableTo: ['student', 'dependent']
  },
  {
    id: 6,
    category: 'personal',
    question: 'Are you married? If yes, is your spouse accompanying you?',
    sampleAnswer: 'No, I am single. OR Yes, I am married to [spouse name], who is a [profession]. They will/will not be accompanying me because [reason]. If accompanying, we have arranged for their dependent visa and have sufficient funds to support both of us.',
    redFlags: ['Inconsistent marital status', 'Not having proper documentation for spouse', 'Unable to explain spouse\'s plans'],
    tips: 'Be clear about your marital status and family plans. Have all relevant documents ready.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 7,
    category: 'personal',
    question: 'What are your hobbies and interests?',
    sampleAnswer: 'I enjoy [hobbies] which have helped me develop [skills]. I am also interested in [relevant interests] which aligns with my career goals. I look forward to experiencing Ireland\'s [cultural activities] and possibly joining [clubs/societies] at the university.',
    redFlags: ['Mentioning hobbies that don\'t match your profile', 'No genuine interests', 'Hobbies that might raise concerns'],
    tips: 'Be genuine and connect your interests to your personal development or career goals.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 8,
    category: 'personal',
    question: 'How do you plan to cope with homesickness and cultural differences?',
    sampleAnswer: 'I understand that adapting to a new culture takes time. I plan to stay connected with family through regular video calls. I am also looking forward to joining student communities and cultural societies at the university. Ireland\'s welcoming nature and the presence of a vibrant Indian community will help me adjust. I see this as an opportunity for personal growth.',
    redFlags: ['Saying you won\'t feel homesick', 'No plan for cultural adaptation', 'Showing dependency on family'],
    tips: 'Show maturity and realistic expectations about living abroad.',
    difficulty: 'Medium',
    applicableTo: ['student', 'work', 'dependent', 'graduate']
  },
  {
    id: 9,
    category: 'personal',
    question: 'What is your current address and how long have you lived there?',
    sampleAnswer: 'I currently reside at [full address] in [city], [state], India. I have been living at this address for [duration]. My family has been based here for [number] years, and this is where I will return during breaks from my studies.',
    redFlags: ['Vague address details', 'Recent address changes without explanation', 'Address not matching documents'],
    tips: 'Ensure your address matches all your official documents. Be prepared to show proof of residence.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 10,
    category: 'personal',
    question: 'Have you ever been refused a visa to any country?',
    sampleAnswer: 'No, I have never been refused a visa to any country. All my previous visa applications have been approved, and I have always complied with immigration rules. OR Yes, I was refused a visa to [country] in [year] because [reason]. Since then, I have [addressed the issue/improved my profile].',
    redFlags: ['Lying about previous refusals', 'Not knowing details of past refusals', 'Blaming the embassy for refusal'],
    tips: 'Always be honest about visa refusals. Immigration systems share data, and lying will result in automatic rejection.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },

  // Education/Course (15 questions for students)
  {
    id: 11,
    category: 'education',
    question: 'Why did you choose this particular course and university?',
    sampleAnswer: 'I chose [Course Name] at [University] because it offers [specific features like curriculum, research opportunities, industry connections]. The program is ranked [ranking] globally and has strong ties with companies like [companies]. The modules on [specific subjects] align perfectly with my career goals in [field]. Additionally, Ireland\'s reputation in [industry] makes it the ideal place to study this subject.',
    redFlags: ['Not knowing course details', 'Choosing university based only on ranking', 'Unable to explain course relevance'],
    tips: 'Research your course thoroughly. Know the modules, faculty, and unique features of the program.',
    difficulty: 'Medium',
    applicableTo: ['student']
  },
  {
    id: 12,
    category: 'education',
    question: 'How does this course relate to your previous education?',
    sampleAnswer: 'My undergraduate degree in [subject] provided me with a strong foundation in [areas]. This master\'s program will help me specialize in [specific area] and gain advanced knowledge in [subjects]. The progression is natural and will enhance my career prospects in [industry].',
    redFlags: ['Unrelated course choice', 'Unable to explain academic progression', 'Course not matching background'],
    tips: 'Show a clear academic progression. If changing fields, explain how your background still applies.',
    difficulty: 'Medium',
    applicableTo: ['student']
  },
  {
    id: 13,
    category: 'education',
    question: 'What are the modules/subjects you will study in this course?',
    sampleAnswer: 'The course includes modules such as [list 4-5 key modules]. In the first semester, I will study [subjects]. The second semester covers [subjects]. I am particularly excited about [specific module] because [reason]. The course also includes a [project/internship/thesis] component.',
    redFlags: ['Not knowing course structure', 'Unable to name subjects', 'Generic answers'],
    tips: 'Review your course curriculum before the interview. Be specific about modules and their relevance.',
    difficulty: 'Medium',
    applicableTo: ['student']
  },
  {
    id: 14,
    category: 'education',
    question: 'What is the duration of your course and when does it start?',
    sampleAnswer: 'The course duration is [X] months/years, starting in [Month Year]. The academic year begins with orientation in [date], and classes commence from [date]. I have already received my unconditional offer letter confirming these details.',
    redFlags: ['Not knowing course dates', 'Inconsistent information', 'Uncertainty about duration'],
    tips: 'Know exact dates from your offer letter. Be clear about semester structure.',
    difficulty: 'Easy',
    applicableTo: ['student']
  },
  {
    id: 15,
    category: 'education',
    question: 'Why didn\'t you pursue this course in India?',
    sampleAnswer: 'While India has excellent educational institutions, this specific program in Ireland offers [unique features like industry partnerships, research facilities, international exposure]. The opportunity to learn from [specific faculty] and access to [resources] is not available in Indian institutions. Additionally, the global recognition of an Irish degree will open international career opportunities.',
    redFlags: ['Saying Indian education is inferior', 'Not researching Indian alternatives', 'Only focusing on migration benefits'],
    tips: 'Focus on what\'s unique about the Irish program, not on criticizing Indian education.',
    difficulty: 'Medium',
    applicableTo: ['student']
  },
  {
    id: 16,
    category: 'education',
    question: 'What research have you done about the university and its facilities?',
    sampleAnswer: 'I have thoroughly researched [University]. It was established in [year] and is known for [specializations]. The campus has [facilities like libraries, labs, sports]. The university has produced notable alumni like [names]. It\'s ranked [ranking] for [subject] and has partnerships with companies like [companies]. I\'ve also connected with current students who shared their experiences.',
    redFlags: ['No research done', 'Only knowing basic information', 'Unable to name facilities'],
    tips: 'Visit the university website, read about faculty, facilities, and student life. Connect with current students if possible.',
    difficulty: 'Medium',
    applicableTo: ['student']
  },
  {
    id: 17,
    category: 'education',
    question: 'What are your career plans after completing this course?',
    sampleAnswer: 'After completing my [degree], I plan to return to India and work in [industry] as a [role]. The skills I gain will be valuable for [specific applications in India]. I have already identified companies like [Indian companies] that hire professionals with this qualification. The starting salary for this role in India is [amount], which shows strong demand for this specialization.',
    redFlags: ['Saying you want to settle in Ireland', 'No clear career plan', 'Unrealistic salary expectations'],
    tips: 'Have a clear career plan focused on returning to India. Research job market and salary expectations.',
    difficulty: 'Medium',
    applicableTo: ['student']
  },
  {
    id: 18,
    category: 'education',
    question: 'How will this course benefit your career in India?',
    sampleAnswer: 'This course will equip me with [specific skills] that are in high demand in India\'s growing [industry]. Companies like [Indian companies] are actively hiring professionals with international exposure in this field. The global perspective and practical skills I gain will make me a strong candidate for roles like [positions] in India.',
    redFlags: ['No connection to Indian job market', 'Vague career benefits', 'Focus on staying abroad'],
    tips: 'Research the Indian job market for your field. Show how international education adds value.',
    difficulty: 'Medium',
    applicableTo: ['student']
  },
  {
    id: 19,
    category: 'education',
    question: 'Have you received any scholarships? If not, why?',
    sampleAnswer: 'Yes, I have received a [scholarship name] worth [amount] based on my [merit/achievements]. OR I applied for scholarships but was not selected. However, my family has sufficient funds to cover all expenses. I plan to apply for internal university scholarships once I demonstrate academic excellence in the first semester.',
    redFlags: ['Not applying for scholarships', 'Unable to explain why no scholarship', 'Expecting guaranteed scholarships'],
    tips: 'Apply for relevant scholarships. If you didn\'t get one, explain your financial preparedness.',
    difficulty: 'Easy',
    applicableTo: ['student']
  },
  {
    id: 20,
    category: 'education',
    question: 'What is your academic background and GPA/percentage?',
    sampleAnswer: 'I completed my [degree] from [University] in [year] with [GPA/percentage]. I scored particularly well in subjects like [relevant subjects] which are directly related to my proposed course. My academic record demonstrates my ability to handle rigorous coursework.',
    redFlags: ['Low grades without explanation', 'Inconsistent academic information', 'Not knowing exact scores'],
    tips: 'Know your exact scores. If you have low grades, be prepared to explain and show improvement.',
    difficulty: 'Easy',
    applicableTo: ['student']
  },
  {
    id: 21,
    category: 'education',
    question: 'Did you consider other countries or universities? Why Ireland?',
    sampleAnswer: 'Yes, I considered universities in [countries]. However, I chose Ireland because [specific reasons like course quality, industry connections, post-study work rights, English-speaking country, welcoming culture]. The [specific feature] of this program is not available elsewhere. Ireland\'s [industry] sector and the Third Level Graduate Scheme make it the best choice for my goals.',
    redFlags: ['Not researching other options', 'Choosing Ireland only for easy immigration', 'Unable to justify choice'],
    tips: 'Show you made an informed decision by comparing options. Focus on academic and professional reasons.',
    difficulty: 'Medium',
    applicableTo: ['student']
  },
  {
    id: 22,
    category: 'education',
    question: 'What English language test did you take? What was your score?',
    sampleAnswer: 'I took the [IELTS/TOEFL/PTE] exam on [date] and scored [score]. My section scores were: Listening [score], Reading [score], Writing [score], Speaking [score]. This meets/exceeds the university\'s requirement of [required score]. I prepared for [duration] using [preparation methods].',
    redFlags: ['Score below requirement', 'Not knowing section scores', 'Recent exam without preparation'],
    tips: 'Know your exact scores and how they compare to requirements. Be prepared to explain if any section is low.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work']
  },
  {
    id: 23,
    category: 'education',
    question: 'What will you do if your visa is refused?',
    sampleAnswer: 'While I am confident in my application, if refused, I would first understand the reasons for refusal. I would then address those concerns and reapply with stronger documentation. Alternatively, I would continue my [current activity] in India and explore other opportunities. However, I am hopeful that won\'t be necessary as I have prepared thoroughly.',
    redFlags: ['Saying you will give up', 'Showing desperation', 'No backup plan'],
    tips: 'Show maturity and realistic thinking. Having a backup plan shows you\'re not desperate.',
    difficulty: 'Medium',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 24,
    category: 'education',
    question: 'Do you plan to work part-time during your studies?',
    sampleAnswer: 'Yes, I am aware that as a Stamp 2 visa holder, I can work up to 20 hours per week during term time and 40 hours during holidays. I plan to work part-time to gain industry experience and cover some living expenses. However, my primary focus will be on my studies, and I have sufficient funds to support myself without working.',
    redFlags: ['Planning to work more than allowed', 'Depending on part-time work for fees', 'Not knowing work regulations'],
    tips: 'Know the work regulations. Emphasize that work is secondary to studies.',
    difficulty: 'Easy',
    applicableTo: ['student']
  },
  {
    id: 25,
    category: 'education',
    question: 'What accommodation have you arranged in Ireland?',
    sampleAnswer: 'I have applied for on-campus accommodation at [university residence], which costs [amount] per month. I have also researched off-campus options in [area] as backup. The university provides support for finding accommodation, and I have budgeted [amount] for housing expenses.',
    redFlags: ['No accommodation plan', 'Unrealistic budget', 'Not knowing accommodation costs'],
    tips: 'Have a clear accommodation plan with costs. Research both on-campus and off-campus options.',
    difficulty: 'Easy',
    applicableTo: ['student']
  },

  // Financial Support (10 questions)
  {
    id: 26,
    category: 'financial',
    question: 'Who is sponsoring your education/stay? What is their income?',
    sampleAnswer: 'My [father/mother/other] is sponsoring my education. They work as [profession] at [company] with an annual income of [amount]. They have savings of [amount] and have already transferred [amount] to my education loan account. I have attached their income tax returns, salary slips, and bank statements as proof.',
    redFlags: ['Not knowing sponsor\'s income', 'Insufficient funds', 'Unclear source of funds'],
    tips: 'Know your sponsor\'s complete financial details. Ensure funds match the documents.',
    difficulty: 'Easy',
    applicableTo: ['student', 'dependent', 'tourist']
  },
  {
    id: 27,
    category: 'financial',
    question: 'How much is your tuition fee? Have you paid it?',
    sampleAnswer: 'The tuition fee for my course is [amount] per year. I have paid [amount] as the initial deposit, and the remaining [amount] will be paid [timeline]. I have the receipt for the deposit payment. My sponsor has arranged for the full amount through [savings/loan combination].',
    redFlags: ['Not knowing exact fees', 'Not paying required deposit', 'No plan for full payment'],
    tips: 'Know exact fees and payment schedule. Pay at least the required deposit before the interview.',
    difficulty: 'Easy',
    applicableTo: ['student']
  },
  {
    id: 28,
    category: 'financial',
    question: 'What is your total budget for studying in Ireland?',
    sampleAnswer: 'My total budget for the first year is [amount], which includes tuition fees of [amount], accommodation costs of [amount], living expenses of [amount], and other expenses like insurance and flights of [amount]. I have funds readily available for the entire duration of my course, totaling [amount].',
    redFlags: ['Not knowing total costs', 'Insufficient budget', 'Vague financial planning'],
    tips: 'Calculate all expenses including hidden costs. Show you have funds for the entire course duration.',
    difficulty: 'Medium',
    applicableTo: ['student']
  },
  {
    id: 29,
    category: 'financial',
    question: 'Have you taken an education loan? What are the terms?',
    sampleAnswer: 'Yes, I have taken an education loan of [amount] from [Bank Name]. The interest rate is [rate]%, with a repayment period of [years] years. The repayment starts [6 months/1 year] after course completion. My [family member] is the co-applicant, and we have mortgaged [collateral if any].',
    redFlags: ['Not knowing loan terms', 'No loan for large amounts', 'Unrealistic repayment plan'],
    tips: 'Know all loan terms including interest rate, repayment schedule, and collateral.',
    difficulty: 'Medium',
    applicableTo: ['student']
  },
  {
    id: 30,
    category: 'financial',
    question: 'Can you show proof of funds for the entire duration of your course?',
    sampleAnswer: 'Yes, I have [amount] in my savings account, [amount] in fixed deposits, and an education loan sanction letter for [amount]. My sponsor\'s bank statements show consistent savings over the past [months/years]. The total available funds are [amount], which covers all expenses for [duration] years.',
    redFlags: ['Funds only for first year', 'Recent large deposits', 'Unable to explain fund sources'],
    tips: 'Maintain funds in your account for at least 6 months before applying. Avoid sudden large deposits.',
    difficulty: 'Medium',
    applicableTo: ['student', 'dependent']
  },
  {
    id: 31,
    category: 'financial',
    question: 'What will be your monthly expenses in Ireland?',
    sampleAnswer: 'Based on my research, my estimated monthly expenses are: Rent [€800-1200], Food [€200-300], Transport [€100-150], Utilities [€100-150], Personal expenses [€200-300], and Insurance [€50-100]. Total monthly expenses will be approximately €1500-2000, which I have budgeted for.',
    redFlags: ['Unrealistic estimates', 'Not researching actual costs', 'Underestimating expenses'],
    tips: 'Research actual costs in your specific city. Dublin is more expensive than other cities.',
    difficulty: 'Medium',
    applicableTo: ['student', 'work', 'dependent', 'graduate']
  },
  {
    id: 32,
    category: 'financial',
    question: 'How do you plan to manage currency exchange rates?',
    sampleAnswer: 'I have factored in potential currency fluctuations in my budget. My sponsor has set aside a buffer of [amount] for exchange rate variations. We plan to transfer funds through [method] which offers competitive rates. I will also explore part-time work opportunities to offset any unexpected increases in expenses.',
    redFlags: ['No consideration for exchange rates', 'Tight budget without buffer', 'No plan for fund transfers'],
    tips: 'Include a 10-15% buffer for currency fluctuations. Research best ways to transfer money internationally.',
    difficulty: 'Medium',
    applicableTo: ['student', 'work', 'dependent']
  },
  {
    id: 33,
    category: 'financial',
    question: 'What is the source of the funds in your bank account?',
    sampleAnswer: 'The funds in my account come from [sources]: family savings accumulated over [years] from my [parent\'s] salary, income from [family business/property], and fixed deposit maturities. I can provide income tax returns, salary certificates, and property documents as proof of legitimate sources.',
    redFlags: ['Unable to explain source', 'Recent unexplained deposits', 'Inconsistent with declared income'],
    tips: 'Ensure all fund sources are documented and legitimate. Avoid cash deposits without clear source.',
    difficulty: 'Medium',
    applicableTo: ['student', 'work', 'dependent', 'tourist']
  },
  {
    id: 34,
    category: 'financial',
    question: 'Have you purchased travel and health insurance?',
    sampleAnswer: 'Yes, I have purchased comprehensive health insurance from [provider] covering [amount] for medical expenses. The insurance is valid for [duration] and covers hospitalization, emergency care, and repatriation. I have also purchased travel insurance for my journey. The total insurance cost was [amount].',
    redFlags: ['No insurance', 'Not knowing coverage details', 'Inadequate coverage'],
    tips: 'Purchase insurance before the interview. Know your coverage details and ensure it meets Irish requirements.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 35,
    category: 'financial',
    question: 'What assets does your family own in India?',
    sampleAnswer: 'My family owns [property details] worth [amount], [vehicle details], and has investments in [stocks/FD/PPF] worth [amount]. These assets demonstrate our strong ties to India and our financial stability. We have no intention of selling these assets as they represent our family\'s foundation in India.',
    redFlags: ['No assets', 'Planning to sell all assets', 'Unable to provide documentation'],
    tips: 'List all family assets. This shows strong ties to India and financial stability.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },

  // Career/Work (15 questions for workers)
  {
    id: 36,
    category: 'career',
    question: 'What is your current job and salary?',
    sampleAnswer: 'I currently work as a [position] at [company] in [city], India. My responsibilities include [key responsibilities]. I earn [salary] per month/year, and I have been in this role for [duration]. This experience has prepared me well for [next step in career].',
    redFlags: ['Unemployed without explanation', 'Salary not matching documents', 'Vague job description'],
    tips: 'Be specific about your role and responsibilities. Ensure salary matches your documents.',
    difficulty: 'Easy',
    applicableTo: ['work', 'graduate']
  },
  {
    id: 37,
    category: 'career',
    question: 'Why do you want to leave your current job?',
    sampleAnswer: 'While I have gained valuable experience at my current job, I am seeking opportunities that offer [specific growth areas]. The role in Ireland offers [specific benefits like exposure to global projects, advanced technologies, career progression]. This is a natural progression in my career, and I plan to return with enhanced skills.',
    redFlags: ['Speaking negatively about current employer', 'No clear reason', 'Running away from problems'],
    tips: 'Focus on growth opportunities, not on escaping current situation. Be positive about your current role.',
    difficulty: 'Medium',
    applicableTo: ['work']
  },
  {
    id: 38,
    category: 'career',
    question: 'Tell me about the job offer you have in Ireland.',
    sampleAnswer: 'I have received a job offer from [company] for the position of [role]. The salary is [amount] per year. My responsibilities will include [key responsibilities]. The company is in [industry] sector and has [details about company]. I will be working on [specific projects/technologies].',
    redFlags: ['Not knowing job details', 'Salary below market rate', 'Unable to explain role'],
    tips: 'Know all details about your job offer. Research the company thoroughly.',
    difficulty: 'Easy',
    applicableTo: ['work']
  },
  {
    id: 39,
    category: 'career',
    question: 'How did you find this job opportunity?',
    sampleAnswer: 'I found this opportunity through [LinkedIn/recruitment agency/company website/referral]. I applied because [reason]. The recruitment process included [interview rounds]. I was selected because my skills in [areas] matched their requirements. The company has also provided me with a detailed offer letter and contract.',
    redFlags: ['Vague about job search process', 'No clear recruitment process', 'Suspicious job offer'],
    tips: 'Be clear about how you found the job. Genuine job offers have proper recruitment processes.',
    difficulty: 'Easy',
    applicableTo: ['work']
  },
  {
    id: 40,
    category: 'career',
    question: 'What is your Critical Skills Employment Permit? Why do you qualify?',
    sampleAnswer: 'I have been approved for a Critical Skills Employment Permit because my role as [position] is on the Critical Skills Occupations List. My offered salary of [amount] meets the current threshold for this role. My qualifications in [field] and experience in [area] support the permit application.',
    redFlags: ['Not knowing permit type', 'Salary below threshold', 'Role not on critical skills list'],
    tips: 'Know the Critical Skills list and requirements. Ensure your role and salary qualify.',
    difficulty: 'Medium',
    applicableTo: ['work']
  },
  {
    id: 41,
    category: 'career',
    question: 'What will you do after your work permit expires?',
    sampleAnswer: 'After my initial work permit period, I plan to [apply for renewal/apply for Stamp 4/return to India]. If I continue working in Ireland, I will have gained valuable international experience that will enhance my career prospects in India. My long-term goal is to [specific career plan in India].',
    redFlags: ['Planning to overstay', 'No plan after permit expires', 'Uncertain about future'],
    tips: 'Have a clear plan. Know the renewal process or your return plans.',
    difficulty: 'Medium',
    applicableTo: ['work']
  },
  {
    id: 42,
    category: 'career',
    question: 'How does this job fit into your long-term career goals?',
    sampleAnswer: 'This role aligns with my goal to [specific career objective]. Working with [company] will give me exposure to [technologies/markets]. After gaining this experience, I plan to return to India and work in [sector] where there is high demand for professionals with international experience. This role is a stepping stone, not the destination.',
    redFlags: ['No long-term plan', 'Planning to settle abroad', 'Job doesn\'t match career goals'],
    tips: 'Show how this job fits into a larger career plan focused on returning to India.',
    difficulty: 'Medium',
    applicableTo: ['work', 'graduate']
  },
  {
    id: 43,
    category: 'career',
    question: 'What skills do you have that make you suitable for this role?',
    sampleAnswer: 'I have [X] years of experience in [field] with expertise in [specific skills]. I have worked on [projects] that are similar to what I will be doing in this role. My certifications in [areas] and my track record of [achievements] demonstrate my capability. I am confident I can contribute effectively from day one.',
    redFlags: ['Skills not matching job requirements', 'Unable to articulate relevant skills', 'Overstating qualifications'],
    tips: 'Match your skills to the job requirements. Be honest about your capabilities.',
    difficulty: 'Medium',
    applicableTo: ['work', 'graduate']
  },
  {
    id: 44,
    category: 'career',
    question: 'What do you know about the company you will be working for?',
    sampleAnswer: '[Company] is a [type of company] founded in [year], headquartered in [location]. They operate in [sectors] with offices in [countries]. Their notable products/services include [details]. In Ireland, they have [operations details]. I am excited to work with them because [specific reasons related to your career goals].',
    redFlags: ['Not knowing company details', 'Vague about company', 'Company doesn\'t match offer'],
    tips: 'Research the company thoroughly. Know their products, services, and presence in Ireland.',
    difficulty: 'Medium',
    applicableTo: ['work']
  },
  {
    id: 45,
    category: 'career',
    question: 'What is the Graduate Scheme (Stamp 1G) and how does it benefit you?',
    sampleAnswer: 'The Third Level Graduate Scheme (Stamp 1G) allows recent graduates from Irish institutions to remain in Ireland for up to 24 months to seek employment. This gives me time to find a job related to my qualification. During this period, I can work full-time without needing an employment permit, which makes me more attractive to employers.',
    redFlags: ['Not knowing Stamp 1G details', 'Planning to work in unrelated field', 'Misunderstanding the scheme'],
    tips: 'Understand the Stamp 1G conditions and duration. Know your rights and limitations.',
    difficulty: 'Medium',
    applicableTo: ['graduate']
  },
  {
    id: 46,
    category: 'career',
    question: 'Have you resigned from your current job? If not, when will you?',
    sampleAnswer: 'Yes, I have submitted my resignation and my last working day is [date]. I have completed my handover responsibilities. OR I will resign once my visa is approved, with a notice period of [duration]. I have discussed this with my employer, and they are supportive of my decision.',
    redFlags: ['Not planning to resign', 'Issues with current employer', 'Long notice period without plan'],
    tips: 'Have a clear plan for leaving your current job. Know your notice period.',
    difficulty: 'Easy',
    applicableTo: ['work']
  },
  {
    id: 47,
    category: 'career',
    question: 'What salary do you expect in Ireland? How does it compare to India?',
    sampleAnswer: 'My offered salary is [amount] per year, which is [comparison] to typical salaries for this role in Ireland. In India, I currently earn [amount]. While the salary is higher in Ireland, I understand the cost of living is also higher. After expenses, I expect to save [amount], which I will use for [purpose].',
    redFlags: ['Unrealistic salary expectations', 'Not knowing market rates', 'Focusing only on money'],
    tips: 'Research salary ranges for your role in Ireland. Understand the cost of living impact.',
    difficulty: 'Medium',
    applicableTo: ['work', 'graduate']
  },
  {
    id: 48,
    category: 'career',
    question: 'What type of work permit have you applied for?',
    sampleAnswer: 'I have applied for a [Critical Skills Employment Permit / General Employment Permit]. The correct permit depends on the occupation, salary, employer and current eligibility rules. My permit type was recommended by my employer based on my role and salary.',
    redFlags: ['Not knowing permit type', 'Wrong permit for role/salary', 'Unable to explain permit conditions'],
    tips: 'Know your permit type and its conditions. Different permits have different benefits and requirements.',
    difficulty: 'Easy',
    applicableTo: ['work']
  },
  {
    id: 49,
    category: 'career',
    question: 'Do you have any professional certifications relevant to your field?',
    sampleAnswer: 'Yes, I hold certifications in [certifications] from [issuing organizations]. These were obtained in [years] and demonstrate my expertise in [areas]. I am also planning to pursue [future certifications] to further enhance my skills. These certifications are recognized internationally and will add value to my role.',
    redFlags: ['No relevant certifications', 'Fake certifications', 'Unable to verify certifications'],
    tips: 'List relevant certifications. Be prepared to show certificates if asked.',
    difficulty: 'Easy',
    applicableTo: ['work', 'graduate']
  },
  {
    id: 50,
    category: 'career',
    question: 'What projects have you worked on that are relevant to this role?',
    sampleAnswer: 'I have worked on [project names] which involved [technologies/methodologies]. In my current role, I led [project] which resulted in [outcome]. These projects gave me experience in [relevant skills] which directly applies to the work I will be doing in Ireland. I can share more details about specific projects if needed.',
    redFlags: ['No relevant project experience', 'Unable to explain projects', 'Taking credit for others\' work'],
    tips: 'Prepare 2-3 relevant project examples. Be specific about your contribution and outcomes.',
    difficulty: 'Medium',
    applicableTo: ['work', 'graduate']
  },

  // Intent to Return (10 questions)
  {
    id: 51,
    category: 'intent',
    question: 'What ties do you have to India that will ensure your return?',
    sampleAnswer: 'I have strong ties to India including my family (parents, siblings), property worth [amount], and a clear career path in [industry]. My parents live in [city] and I am their primary support. I have job opportunities waiting for me in India with companies like [companies]. I plan to return after [duration] to apply my international experience.',
    redFlags: ['No family ties', 'No property or assets', 'Vague about return plans'],
    tips: 'Document all ties to India - family, property, job prospects. Be specific about your return plans.',
    difficulty: 'Medium',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 52,
    category: 'intent',
    question: 'Do you plan to settle in Ireland permanently?',
    sampleAnswer: 'No, I do not plan to settle permanently in Ireland. My goal is to [study/work] for [duration] and then return to India. My family, career prospects, and long-term goals are all centered in India. The international experience will enhance my value in the Indian job market, where there is high demand for professionals with global exposure.',
    redFlags: ['Expressing desire to settle', 'Vague about return', 'All plans focused on Ireland'],
    tips: 'Be clear about your temporary stay intention. Focus on what you will do after returning to India.',
    difficulty: 'Medium',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 53,
    category: 'intent',
    question: 'What will you do after completing your studies/work in Ireland?',
    sampleAnswer: 'After completing my [studies/work], I will return to India and [specific plan]. I have already [spoken with employers/applied to companies] and there is strong interest in candidates with international experience. The starting salary for this role in India is [amount], and I expect to secure a position within [timeframe] of returning.',
    redFlags: ['No plan for after', 'Planning to extend stay', 'All plans in Ireland'],
    tips: 'Have a concrete plan for your return. Research job prospects and show genuine intent to return.',
    difficulty: 'Medium',
    applicableTo: ['student', 'work', 'graduate']
  },
  {
    id: 54,
    category: 'intent',
    question: 'Do you have a job offer in India after your return?',
    sampleAnswer: 'Yes, I have a conditional offer from [company] in India for the position of [role], valid after I complete my [studies/work]. OR While I don\'t have a confirmed offer, I have been in discussions with [companies] and they have expressed interest. The [industry] sector in India is growing rapidly, and my international qualification will be valuable.',
    redFlags: ['No job prospects', 'Unrealistic job expectations', 'Not researching Indian market'],
    tips: 'A job offer strengthens your case. If not, show you\'ve researched opportunities and have a realistic plan.',
    difficulty: 'Medium',
    applicableTo: ['student', 'work', 'graduate']
  },
  {
    id: 55,
    category: 'intent',
    question: 'What is your family situation? Will they accompany you?',
    sampleAnswer: 'I am [single/married]. My [spouse/parents] will remain in India during my [studies/work]. They have their own [careers/lives] in India and we have strong family ties there. This is another reason I will return - to be with my family. We have discussed this thoroughly and they support my temporary stay abroad.',
    redFlags: ['Family planning to migrate', 'No family ties in India', 'Unsure about family situation'],
    tips: 'Show that your family ties in India are strong and that they support your temporary stay.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 56,
    category: 'intent',
    question: 'Have you ever violated visa conditions in any country?',
    sampleAnswer: 'No, I have never violated visa conditions. I have always respected immigration laws and returned to India before my visa expired. I understand the importance of complying with visa conditions and will continue to do so during my stay in Ireland.',
    redFlags: ['Previous visa violations', 'Overstaying history', 'Working illegally in past'],
    tips: 'Be honest about any past issues. Immigration systems share data, and lying will result in rejection.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 57,
    category: 'intent',
    question: 'What assets do you own in India?',
    sampleAnswer: 'I own [property details] in [location] worth [amount]. I also have investments in [stocks/FD/PPF/mutual funds] worth [amount]. These assets represent my financial foundation in India and are one of the reasons I will return. I have no intention of liquidating these assets.',
    redFlags: ['No assets', 'Planning to sell everything', 'Unable to provide documentation'],
    tips: 'List all assets with approximate values. This shows financial ties to India.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 58,
    category: 'intent',
    question: 'Why should we believe you will return to India?',
    sampleAnswer: 'I have strong reasons to return: my family including [details] lives in India, I own property worth [amount], I have career opportunities with [companies] that value international experience, and my long-term goals are centered in India. I am seeking temporary international exposure to enhance my career, not to migrate. I have always complied with visa conditions in the past.',
    redFlags: ['Weak ties to India', 'All plans abroad', 'Unable to provide evidence of ties'],
    tips: 'Present multiple strong ties to India - family, property, career, and future plans.',
    difficulty: 'Medium',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 59,
    category: 'intent',
    question: 'What is your plan if you don\'t find a job in Ireland after graduation?',
    sampleAnswer: 'If I don\'t find a suitable job within the Graduate Scheme period, I will return to India. I have saved [amount] as an emergency fund for this scenario. The Indian job market for [field] is strong, and my Irish degree will be valuable there. I am prepared for either outcome and have plans for both.',
    redFlags: ['No backup plan', 'Planning to overstay', 'Desperation to stay'],
    tips: 'Show you have realistic backup plans. Desperation raises red flags.',
    difficulty: 'Medium',
    applicableTo: ['graduate']
  },
  {
    id: 60,
    category: 'intent',
    question: 'How long do you plan to stay in Ireland?',
    sampleAnswer: 'I plan to stay for [specific duration] - the duration of my [course/work permit]. For students, this is [X] years for my degree program. For workers, this is [X] years as per my employment permit. I have no intention of extending beyond this period without proper authorization.',
    redFlags: ['Vague about duration', 'Planning to extend indefinitely', 'Duration not matching visa type'],
    tips: 'Be specific about your planned duration. It should match your visa type and stated purpose.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },

  // Ireland Knowledge (10 questions)
  {
    id: 61,
    category: 'ireland',
    question: 'What do you know about Irish culture and society?',
    sampleAnswer: 'Ireland is known for its rich cultural heritage including literature (famous writers like James Joyce, Oscar Wilde), music (traditional Irish music and U2), and sports (Gaelic football, hurling). The Irish people are known for their friendliness and hospitality. The country has a young, diverse population with a growing tech sector. I look forward to experiencing the culture firsthand.',
    redFlags: ['No knowledge of Ireland', 'Stereotypical views only', 'Not interested in culture'],
    tips: 'Research Irish culture, history, and current events. Show genuine interest in the country.',
    difficulty: 'Medium',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 62,
    category: 'ireland',
    question: 'What is the capital of Ireland? Name some major cities.',
    sampleAnswer: 'Dublin is the capital and largest city of Ireland. Other major cities include Cork (second largest, known for its harbor), Galway (cultural hub on the west coast), Limerick (historic city on the River Shannon), and Waterford (oldest city, known for crystal). My [university/company] is located in [city].',
    redFlags: ['Not knowing basic geography', 'Confusing Ireland with Northern Ireland', 'No knowledge of destination city'],
    tips: 'Know basic Irish geography and facts about your destination city.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 63,
    category: 'ireland',
    question: 'What languages are spoken in Ireland?',
    sampleAnswer: 'Ireland has two official languages: Irish (Gaeilge) and English. English is the primary language used in business, education, and daily life. Irish is taught in schools and is the first official language, though it\'s spoken as a first language by a small percentage of the population, mainly in Gaeltacht regions. I will have no language barrier as I am proficient in English.',
    redFlags: ['Not knowing official languages', 'Expecting language barrier', 'Not prepared for English environment'],
    tips: 'Know that English is the primary language. Irish (Gaelic) is the first official language but less commonly spoken.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 64,
    category: 'ireland',
    question: 'What do you know about the Irish education system?',
    sampleAnswer: 'Ireland has a world-class education system with universities like Trinity College Dublin (founded 1592), University College Dublin, and others. The National Framework of Qualifications (NFQ) ensures quality standards. Irish degrees are internationally recognized. The academic year typically runs from September to May with semesters. International students can work part-time during studies.',
    redFlags: ['No knowledge of education system', 'Not knowing about own university', 'Unaware of academic structure'],
    tips: 'Research the Irish education system, especially your institution\'s place in it.',
    difficulty: 'Medium',
    applicableTo: ['student']
  },
  {
    id: 65,
    category: 'ireland',
    question: 'What is the currency of Ireland? What is the exchange rate?',
    sampleAnswer: 'Ireland uses the Euro (€) as its currency, being part of the Eurozone. The current exchange rate is approximately [rate] INR to 1 Euro. I have factored this into my budget planning. I plan to open an Irish bank account after arrival for easier transactions.',
    redFlags: ['Not knowing currency', 'No idea of exchange rate', 'Unprepared for currency difference'],
    tips: 'Know the current exchange rate and have a plan for managing finances in Euro.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 66,
    category: 'ireland',
    question: 'What do you know about Ireland\'s economy and major industries?',
    sampleAnswer: 'Ireland has a strong, modern economy with GDP growth among the highest in Europe. Major industries include technology (Google, Facebook, Apple have European HQs there), pharmaceuticals (major pharmaceutical companies), financial services, and agriculture. The country is known as the "Celtic Tiger" for its rapid economic growth. The tech sector particularly offers excellent opportunities for professionals.',
    redFlags: ['No economic knowledge', 'Outdated information', 'Not knowing about relevant industry'],
    tips: 'Research Ireland\'s economy, especially industries relevant to your field.',
    difficulty: 'Medium',
    applicableTo: ['student', 'work', 'graduate']
  },
  {
    id: 67,
    category: 'ireland',
    question: 'Who is the current leader of Ireland?',
    sampleAnswer: 'Ireland is a parliamentary republic. The current President (head of state) is Michael D. Higgins. The Taoiseach (Prime Minister, head of government) is [current name - check latest]. The government operates from Leinster House in Dublin. Ireland is a member of the European Union but not part of NATO.',
    redFlags: ['Not knowing any leaders', 'Confusing with UK leadership', 'No knowledge of political system'],
    tips: 'Know the current President and Taoiseach. Understand Ireland\'s political system.',
    difficulty: 'Medium',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 68,
    category: 'ireland',
    question: 'What is the difference between Ireland and Northern Ireland?',
    sampleAnswer: 'Ireland (Republic of Ireland) is an independent country and member of the EU. Northern Ireland is part of the United Kingdom. They share the island of Ireland. The border between them is currently open due to the Common Travel Area. The Republic of Ireland uses the Euro, while Northern Ireland uses the British Pound. I will be studying/working in the Republic of Ireland.',
    redFlags: ['Not knowing the difference', 'Confusing the two', 'Not knowing which one they\'re going to'],
    tips: 'Understand the political distinction. Know which part you\'re visiting.',
    difficulty: 'Medium',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 69,
    category: 'ireland',
    question: 'What is the weather like in Ireland? How will you adapt?',
    sampleAnswer: 'Ireland has a temperate maritime climate with mild winters (5-8°C) and cool summers (15-20°C). It rains frequently throughout the year. I have packed appropriate clothing including rain gear and warm layers. I am prepared for the weather difference from India and see it as part of the international experience.',
    redFlags: ['Not knowing climate', 'Unprepared for weather', 'Expecting extreme weather'],
    tips: 'Know the climate and be prepared for rain. Ireland rarely sees extreme temperatures.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'tourist', 'dependent', 'graduate']
  },
  {
    id: 70,
    category: 'ireland',
    question: 'What do you know about the Indian community in Ireland?',
    sampleAnswer: 'There is a vibrant Indian community in Ireland, estimated at around 45,000-50,000 people. There are Indian restaurants, grocery stores, temples, and cultural organizations. Major cities like Dublin have active Indian community groups that celebrate festivals like Diwali and Holi. This community presence will help me stay connected to my culture while experiencing Irish life.',
    redFlags: ['Not knowing about community', 'No interest in connecting', 'Expecting no Indian presence'],
    tips: 'Research the Indian community in your destination city. This shows you\'ve done your homework.',
    difficulty: 'Easy',
    applicableTo: ['student', 'work', 'dependent', 'graduate']
  }
];

// Shuffle icon component - defined before use
const Shuffle = ({ size }) => <span style={{ fontSize: size }}>🔀</span>;

// Interview Modes
const INTERVIEW_MODES = [
  { id: 'practice', name: 'Practice Mode', icon: Lightbulb, description: 'See hints, take your time' },
  { id: 'mock', name: 'Mock Test', icon: Clock, description: 'Timed, no hints' },
  { id: 'random', name: 'Random Questions', icon: Shuffle, description: 'Mix of all categories' }
];

const IrelandVisaInterviewSimulator = () => {
  const [selectedVisaType, setSelectedVisaType] = useState(null);
  const [selectedMode, setSelectedMode] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [showSampleAnswer, setShowSampleAnswer] = useState(false);
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [interviewComplete, setInterviewComplete] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);

  // Filter questions based on visa type and categories
  const filteredQuestions = useMemo(() => {
    if (!selectedVisaType) return [];
    
    let filtered = QUESTION_BANK.filter(q => q.applicableTo.includes(selectedVisaType));
    
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(q => selectedCategories.includes(q.category));
    }
    
    return filtered;
  }, [selectedVisaType, selectedCategories]);

  // Timer effect
  useEffect(() => {
    let interval;
    if (isTimerRunning && selectedMode === 'mock') {
      interval = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, selectedMode]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startInterview = () => {
    let selectedQuestions = [...filteredQuestions];
    
    if (selectedMode === 'random') {
      // Shuffle and pick random questions
      selectedQuestions = selectedQuestions.sort(() => Math.random() - 0.5).slice(0, 10);
    } else {
      // Pick questions from selected categories or all if none selected
      selectedQuestions = selectedQuestions.slice(0, 10);
    }
    
    setQuestions(selectedQuestions);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setInterviewStarted(true);
    setInterviewComplete(false);
    setTimeElapsed(0);
    setIsTimerRunning(true);
    setShowSampleAnswer(false);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setShowSampleAnswer(false);
    } else {
      completeInterview();
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setShowSampleAnswer(false);
    }
  };

  const handleScoreAnswer = (score) => {
    const currentQuestion = questions[currentQuestionIndex];
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: score
    }));
  };

  const completeInterview = () => {
    setIsTimerRunning(false);
    setInterviewComplete(true);
  };

  const resetInterview = () => {
    setSelectedVisaType(null);
    setSelectedMode(null);
    setSelectedCategories([]);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setInterviewStarted(false);
    setInterviewComplete(false);
    setTimeElapsed(0);
    setIsTimerRunning(false);
    setShowSampleAnswer(false);
  };

  const calculateResults = () => {
    const totalPossible = questions.length * 5;
    const earnedScore = Object.values(answers).reduce((sum, score) => sum + score, 0);
    const percentage = Math.round((earnedScore / totalPossible) * 100);
    
    let grade, message, color;
    if (percentage >= 80) {
      grade = 'Excellent';
      message = 'You are well-prepared for your visa interview!';
      color = '#10b981';
    } else if (percentage >= 60) {
      grade = 'Good';
      message = 'You have a solid foundation. Keep practicing!';
      color = '#3b82f6';
    } else if (percentage >= 40) {
      grade = 'Needs Improvement';
      message = 'Focus on the areas where you scored low.';
      color = '#f59e0b';
    } else {
      grade = 'Requires Preparation';
      message = 'Please study the sample answers and practice more.';
      color = '#ef4444';
    }

    return { earnedScore, totalPossible, percentage, grade, message, color };
  };

  const downloadQuestions = () => {
    const content = questions.map((q, i) => 
      `Question ${i + 1}: ${q.question}\n\nSample Answer:\n${q.sampleAnswer}\n\nRed Flags:\n${q.redFlags.join('\n')}\n\nTips:\n${q.tips}\n\n${'='.repeat(50)}\n`
    ).join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ireland-visa-interview-questions.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Setup Screen
  if (!interviewStarted && !interviewComplete) {
    return (
      <div className="interview-simulator">
        <div className="simulator-header">
          <h2>🎤 Ireland Visa Interview Simulator</h2>
          <p className="simulator-subtitle">
            Practice with 50+ real interview questions specific to Ireland visa applications
          </p>
        </div>

        {/* Visa Type Selection */}
        <div className="setup-section">
          <h3>
            <User size={20} />
            Select Your Visa Type
          </h3>
          <div className="visa-type-grid">
            {VISA_TYPES.map(visa => (
              <button
                key={visa.id}
                className={`visa-type-card ${selectedVisaType === visa.id ? 'selected' : ''}`}
                onClick={() => setSelectedVisaType(visa.id)}
              >
                <span className="visa-icon">{visa.icon}</span>
                <span className="visa-name">{visa.name}</span>
                <span className="visa-desc">{visa.description}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Interview Mode Selection */}
        {selectedVisaType && (
          <div className="setup-section">
            <h3>
              <Target size={20} />
              Choose Interview Mode
            </h3>
            <div className="mode-grid">
              {INTERVIEW_MODES.map(mode => (
                <button
                  key={mode.id}
                  className={`mode-card ${selectedMode === mode.id ? 'selected' : ''}`}
                  onClick={() => setSelectedMode(mode.id)}
                >
                  <mode.icon size={24} />
                  <span className="mode-name">{mode.name}</span>
                  <span className="mode-desc">{mode.description}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Category Selection */}
        {selectedVisaType && selectedMode && (
          <div className="setup-section">
            <h3>
              <FileText size={20} />
              Select Question Categories (Optional)
            </h3>
            <p className="helper-text">Leave empty to include all categories</p>
            <div className="category-grid">
              {Object.entries(QUESTION_CATEGORIES).map(([key, cat]) => {
                const IconComponent = cat.icon;
                const questionCount = QUESTION_BANK.filter(
                  q => q.category === key && q.applicableTo.includes(selectedVisaType)
                ).length;
                
                return (
                  <button
                    key={key}
                    className={`category-card ${selectedCategories.includes(key) ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedCategories(prev =>
                        prev.includes(key)
                          ? prev.filter(c => c !== key)
                          : [...prev, key]
                      );
                    }}
                    style={{ '--cat-color': cat.color }}
                  >
                    <IconComponent size={20} />
                    <span className="cat-name">{cat.name}</span>
                    <span className="cat-count">{questionCount} questions</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Start Button */}
        {selectedVisaType && selectedMode && (
          <div className="start-section">
            <div className="interview-summary">
              <div className="summary-item">
                <span className="summary-label">Visa Type:</span>
                <span className="summary-value">
                  {VISA_TYPES.find(v => v.id === selectedVisaType)?.name}
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Mode:</span>
                <span className="summary-value">
                  {INTERVIEW_MODES.find(m => m.id === selectedMode)?.name}
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Questions:</span>
                <span className="summary-value">{Math.min(filteredQuestions.length, 10)}</span>
              </div>
            </div>
            <button className="start-btn" onClick={startInterview}>
              <Play size={20} />
              Start Interview Practice
            </button>
          </div>
        )}
      </div>
    );
  }

  // Interview Complete Screen
  if (interviewComplete) {
    const results = calculateResults();
    const categoryScores = {};
    
    questions.forEach(q => {
      if (!categoryScores[q.category]) {
        categoryScores[q.category] = { earned: 0, total: 0, count: 0 };
      }
      categoryScores[q.category].earned += answers[q.id] || 0;
      categoryScores[q.category].total += 5;
      categoryScores[q.category].count += 1;
    });

    return (
      <div className="interview-simulator">
        <div className="simulator-header">
          <h2>🎉 Interview Complete!</h2>
        </div>

        <div className="results-container">
          {/* Score Card */}
          <div className="score-card" style={{ borderColor: results.color }}>
            <div className="score-circle" style={{ background: `conic-gradient(${results.color} ${results.percentage * 3.6}deg, #2d3748 0deg)` }}>
              <span className="score-value">{results.percentage}%</span>
            </div>
            <div className="score-details">
              <h3 style={{ color: results.color }}>{results.grade}</h3>
              <p>{results.message}</p>
              <div className="score-stats">
                <div className="stat">
                  <span className="stat-value">{results.earnedScore}</span>
                  <span className="stat-label">Points Earned</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{questions.length}</span>
                  <span className="stat-label">Questions</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{formatTime(timeElapsed)}</span>
                  <span className="stat-label">Time Taken</span>
                </div>
              </div>
            </div>
          </div>

          {/* Pass/Fail Indicator */}
          <div className={`pass-fail-indicator ${results.percentage >= 60 ? 'pass' : 'fail'}`}>
            {results.percentage >= 60 ? (
              <>
                <CheckCircle size={24} />
                <span>PASS - You're on the right track!</span>
              </>
            ) : (
              <>
                <XCircle size={24} />
                <span>NEEDS IMPROVEMENT - Keep practicing!</span>
              </>
            )}
          </div>

          {/* Category Breakdown */}
          <div className="category-breakdown">
            <h3>Performance by Category</h3>
            <div className="category-bars">
              {Object.entries(categoryScores).map(([cat, scores]) => {
                const percentage = Math.round((scores.earned / scores.total) * 100);
                const catInfo = QUESTION_CATEGORIES[cat];
                return (
                  <div key={cat} className="category-bar-item">
                    <div className="bar-header">
                      <span className="bar-label">{catInfo?.name || cat}</span>
                      <span className="bar-score">{percentage}%</span>
                    </div>
                    <div className="bar-track">
                      <div 
                        className="bar-fill" 
                        style={{ 
                          width: `${percentage}%`,
                          backgroundColor: catInfo?.color || '#169B62'
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Improvement Suggestions */}
          <div className="improvement-section">
            <h3>
              <TrendingUp size={20} />
              Improvement Suggestions
            </h3>
            <ul className="suggestions-list">
              {results.percentage < 80 && (
                <li>Review the sample answers for questions you scored low on</li>
              )}
              {results.percentage < 60 && (
                <li>Practice more with the Practice Mode to see hints and tips</li>
              )}
              {!selectedCategories.includes('intent') && (
                <li>Focus on "Intent to Return" questions - this is crucial for visa approval</li>
              )}
              {timeElapsed > 600 && (
                <li>Try to answer more concisely - aim for 2-3 minutes per question</li>
              )}
              <li>Research your specific university/company thoroughly before the real interview</li>
              <li>Practice answering questions out loud to build confidence</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="results-actions">
            <button className="action-btn primary" onClick={resetInterview}>
              <RefreshCw size={18} />
              Practice Again
            </button>
            <button className="action-btn secondary" onClick={downloadQuestions}>
              <Download size={18} />
              Download Questions
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Interview In Progress
  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = answers[currentQuestion?.id];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <div className="interview-simulator">
      {/* Progress Header */}
      <div className="interview-header">
        <div className="progress-info">
          <span className="question-counter">
            Question {currentQuestionIndex + 1} of {questions.length}
          </span>
          <div className="interview-progress-bar">
            <div className="interview-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
        {selectedMode === 'mock' && (
          <div className="timer">
            <Clock size={18} />
            <span>{formatTime(timeElapsed)}</span>
          </div>
        )}
      </div>

      {/* Question Card */}
      <div className="question-card">
        <div className="question-meta">
          <span 
            className="question-category"
            style={{ color: QUESTION_CATEGORIES[currentQuestion?.category]?.color }}
          >
            {QUESTION_CATEGORIES[currentQuestion?.category]?.name}
          </span>
          <span className={`difficulty ${currentQuestion?.difficulty?.toLowerCase()}`}>
            {currentQuestion?.difficulty}
          </span>
        </div>
        
        <h3 className="interview-question-text">{currentQuestion?.question}</h3>

        {/* Self-scoring for Practice Mode */}
        {selectedMode === 'practice' && (
          <div className="practice-controls">
            <p className="practice-hint">After answering, rate how well you did:</p>
            <div className="score-buttons">
              {[1, 2, 3, 4, 5].map(score => (
                <button
                  key={score}
                  className={`score-btn ${currentAnswer === score ? 'selected' : ''}`}
                  onClick={() => handleScoreAnswer(score)}
                >
                  {score}
                </button>
              ))}
            </div>
            <div className="score-labels">
              <span>Poor</span>
              <span>Excellent</span>
            </div>
          </div>
        )}

        {/* Show Sample Answer Button */}
        <button 
          className="toggle-answer-btn"
          onClick={() => setShowSampleAnswer(!showSampleAnswer)}
        >
          {showSampleAnswer ? 'Hide' : 'Show'} Sample Answer
        </button>

        {/* Sample Answer Section */}
        {showSampleAnswer && (
          <div className="answer-section">
            <div className="sample-answer">
              <h4>
                <CheckCircle size={18} />
                Sample Answer
              </h4>
              <p>{currentQuestion?.sampleAnswer}</p>
            </div>
            
            <div className="red-flags">
              <h4>
                <AlertTriangle size={18} />
                Red Flags to Avoid
              </h4>
              <ul>
                {currentQuestion?.redFlags?.map((flag, i) => (
                  <li key={i}>{flag}</li>
                ))}
              </ul>
            </div>
            
            <div className="tips">
              <h4>
                <Lightbulb size={18} />
                Tips for Answering
              </h4>
              <p>{currentQuestion?.tips}</p>
            </div>
          </div>
        )}

        {/* Mock Mode Scoring */}
        {selectedMode === 'mock' && (
          <div className="mock-controls">
            <p className="mock-hint">Rate your answer (this will be revealed at the end):</p>
            <div className="score-buttons">
              {[1, 2, 3, 4, 5].map(score => (
                <button
                  key={score}
                  className={`score-btn ${currentAnswer === score ? 'selected' : ''}`}
                  onClick={() => handleScoreAnswer(score)}
                >
                  {score}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="navigation-buttons">
        <button 
          className="nav-btn back"
          onClick={handlePrevQuestion}
          disabled={currentQuestionIndex === 0}
        >
          <ChevronLeft size={18} />
          Previous
        </button>
        
        {currentQuestionIndex === questions.length - 1 ? (
          <button className="nav-btn complete" onClick={completeInterview}>
            <Award size={18} />
            Complete Interview
          </button>
        ) : (
          <button className="nav-btn next" onClick={handleNextQuestion}>
            Next
            <ChevronRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
};

export default IrelandVisaInterviewSimulator;
